import {
	createActionsHook,
	createContainer,
	createHook,
	createStateHook,
	createStore,
	defaults,
	type Action,
	type BoundActions,
	type HookActionsFunction,
	type HookReturnValue,
	type HookStateFunction,
	type OverrideContainerComponent,
} from 'react-sweet-state';

import {
	type Product,
	type ProductData,
	type RemoteAction,
	type RemoteActionsMetas,
	type RemoteActionsResponse,
} from '../../common/types';

import { actions, type RemoteActionsActions } from './actions';

// jira freezes the global defaults object
if (!Object.isFrozen(defaults)) {
	defaults.devtools = process.env.NODE_ENV === 'development' && !process.env.CI;
}

export type RemoteActionsStoreState = {
	isLoaded: boolean;
	lastUpdatedAt: number;
	meta: RemoteActionsMetas;
	product: Product | undefined;
	remoteActions: { [id: string]: RemoteAction };
};

const Store = createStore<RemoteActionsStoreState, RemoteActionsActions>({
	initialState: {
		meta: {},
		remoteActions: {},
		lastUpdatedAt: 0,
		isLoaded: false,
		product: undefined,
	},
	actions,
	name: 'remoteActionsStore',
});

export const RemoteActionsContainer: OverrideContainerComponent<{
	product: Product;
}> = createContainer<RemoteActionsStoreState, RemoteActionsActions, { product: Product }>(Store, {
	onInit:
		() =>
		({ dispatch }) => {
			dispatch(actions.initStore());
		},
});

export const useRemoteActions = <T = RemoteActionsStoreState>(
	selector?: (state: RemoteActionsStoreState) => T,
): HookReturnValue<
	T,
	BoundActions<
		RemoteActionsStoreState,
		{
			clearActionId: (actionId: string) => Action<
				RemoteActionsStoreState,
				{
					product: Product;
				}
			>;
			initStore: () => Action<
				RemoteActionsStoreState,
				{
					product: Product;
				}
			>;
			processRemoteActions: (
				productData: ProductData,
				remoteActionsResponse: RemoteActionsResponse,
			) => Action<RemoteActionsStoreState>;
			saveActionPerformedAttempt: (actionId: string) => Action<RemoteActionsStoreState>;
		}
	>
> =>
	createHook<RemoteActionsStoreState, RemoteActionsActions, T>(Store, {
		selector,
	})();

export const useRemoteActionsData: HookStateFunction<
	{
		[id: string]: RemoteAction;
	},
	void
> = createStateHook(Store, {
	selector: (state) => state.remoteActions,
});

export const useMeta: HookStateFunction<RemoteActionsMetas, void> = createStateHook(Store, {
	selector: (state) => state.meta,
});

export const useRemoteActionLastUpdatedAt: HookStateFunction<number, void> = createStateHook(
	Store,
	{
		selector: (state) => state.lastUpdatedAt,
	},
);

export const useRemoteActionsStoreActions: HookActionsFunction<
	BoundActions<
		RemoteActionsStoreState,
		{
			clearActionId: (actionId: string) => Action<
				RemoteActionsStoreState,
				{
					product: Product;
				}
			>;
			initStore: () => Action<
				RemoteActionsStoreState,
				{
					product: Product;
				}
			>;
			processRemoteActions: (
				productData: ProductData,
				remoteActionsResponse: RemoteActionsResponse,
			) => Action<RemoteActionsStoreState>;
			saveActionPerformedAttempt: (actionId: string) => Action<RemoteActionsStoreState>;
		}
	>
> = createActionsHook<RemoteActionsStoreState, RemoteActionsActions>(Store);
